/**
 * NIP checksum weights configuration
 */
const NIP_CHECKSUM_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7] as const;

/**
 * Normalizes NIP by removing dashes and keeping only digits
 * @param nip - The NIP to normalize
 * @returns Normalized NIP (10 digits only)
 */
const normalizeNIP = (nip: string): string => {
  return nip.replace(/-/g, "");
};

/**
 * Validates NIP checksum
 * Based on: http://www.algorytm.org/numery-identyfikacyjne/nip.html
 * @param nip - The NIP to validate (can be with or without dashes)
 * @returns True if the NIP checksum is valid, false otherwise
 */
export const validateNIPChecksum = (nip: string): boolean => {
  // If nip is not a string, return false
  if (!nip) {
    return false;
  }

  const normalized = normalizeNIP(nip);

  // Must have exactly 10 digits
  if (!/^\d{10}$/.test(normalized)) {
    return false;
  }

  // Calculate checksum: sum of first 9 digits × weights, then modulo 11
  const sum = NIP_CHECKSUM_WEIGHTS.reduce(
    (acc, weight, index) => acc + Number(normalized[index]) * weight,
    0,
  );

  const checksum = sum % 11;

  // If checksum is 10, the NIP is invalid (according to the algorithm)
  if (checksum === 10) {
    return false;
  }

  // The 10th digit must equal the checksum
  const lastDigit = Number(normalized[9]);
  return checksum === lastDigit;
};
